import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn';
import { CollapseCard, TextField, IconButton, Button } from '@totalsoft/rocket-ui';
import DocumentScoringResultSummaryAlpha from './DocumentScoringResultSummaryAlpha';
import { withRights } from '../../../../../providers/RightsConsumer';
import { RightsContext } from 'providers/RightsContext';

export const DocumentScoringResultComponentAlpha = ({
  scoringResult,
  t,
  scoringInProgress,
  rights,
  generateScoring,
  viewScoringHistory
}) => {
  const { isImage, folderScoringHistoryRead } = useContext(RightsContext);

  return (
    <CollapseCard
      icon={AssignmentTurnedIn}
      title={t('DocumentEdit.Scoring.ScoringResult.Title')}
      subheader={<DocumentScoringResultSummaryAlpha scoringResult={scoringResult} />}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled
            fullWidth
            value={
              scoringResult.hasScoringResult
                ? scoringResult.scoringResult || ''
                : t('DocumentEdit.Scoring.ScoringResult.ScoringResultNotApplicable')
            }
            label={t('DocumentEdit.Scoring.ScoringResult.ScoringResult')}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            disabled
            fullWidth
            value={
              scoringResult.hasScoringResult
                ? scoringResult.scoringClass || ''
                : t('DocumentEdit.Scoring.ScoringResult.ScoringResultNotApplicable')
            }
            label={t('DocumentEdit.Scoring.ScoringResult.ScoringClassAlpha')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled
            fullWidth
            value={scoringResult.knockoutCriterion || ''}
            label={t('DocumentEdit.Scoring.ScoringResult.KnockoutCriterion')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled
            fullWidth
            value={scoringResult.businessPolicyRules || ''}
            label={t('DocumentEdit.Scoring.ScoringResult.BusinessPolicyRules')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled
            fullWidth
            value={scoringResult.hardCurrency || ''}
            label={t('DocumentEdit.Scoring.ScoringResult.HardCurrency')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled
            fullWidth
            value={scoringResult.minimumLendingStandard || ''}
            label={t('DocumentEdit.Scoring.ScoringResult.MinimumLendingStandard')}
          />
        </Grid>
        {scoringResult?.partnerType?.isPF && (
          <Grid item xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              value={scoringResult.indebtednessDegree || ''}
              label={t('DocumentEdit.Scoring.ScoringResult.IndebtednessCriterion')}
            />
          </Grid>
        )}
        {scoringResult?.partnerType?.isPFA && (
          <Grid item xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              value={scoringResult.incomeCoverage || ''}
              label={t('DocumentEdit.Scoring.ScoringResult.IncomeCoverage')}
              endAdornment={
                <IconButton
                  disabled
                  color='primary'
                  variant='text'
                  size='tiny'
                  tooltip={t('DocumentEdit.Scoring.ScoringResult.IncomeCoverageDetails')}
                >
                  <InfoIcon />
                </IconButton>
              }
            />
          </Grid>
        )}
        <Grid item xs={12} sm={3} md={4}>
          <TextField
            disabled
            fullWidth
            value={scoringResult.downPaymentRating || ''}
            label={t('DocumentEdit.Scoring.ScoringResult.DownPaymentRating')}
            endAdornment={
              <IconButton disabled tooltip={t('DocumentEdit.Scoring.ScoringResult.DownPaymentRatingDetails')} variant='text'>
                <InfoIcon />
              </IconButton>
            }
          />
        </Grid>
        <Grid item xs={12} sm={3} md={4}>
          <TextField
            disabled
            fullWidth
            value={scoringResult.additionalGuarantees || ''}
            label={t('DocumentEdit.Scoring.ScoringResult.AdditionalGuarantees')}
            endAdornment={
              <IconButton disabled tooltip={t('DocumentEdit.Scoring.ScoringResult.AdditionalGuaranteesDetails')} variant='text'>
                <InfoIcon />
              </IconButton>
            }
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <FormControlLabel
            control={<Checkbox checked={scoringResult.automaticApproval} disabled={true} color='primary' name='anafGrid' />}
            label={t('DocumentEdit.Scoring.ScoringResult.AutomaticApproval')}
          />
        </Grid>
        <Grid item xs={12} sm={3} md={2}>
          <FormControlLabel
            control={<Checkbox checked={scoringResult.anafGrid} disabled={true} color='primary' name='anafGrid' />}
            label={t('DocumentEdit.Scoring.ScoringResult.AnafGrid')}
          />
        </Grid>
        <Grid item xs={12}>
          {!isImage && (
            <Grid container spacing={1}>
              <Grid item>
                <Button onClick={generateScoring} disabled={rights.readOnlyFull || scoringInProgress || !rights.folderComputeScoring}>
                  <HowToRegIcon />
                  {scoringInProgress ? t('DocumentEdit.Scoring.ScoringInProgress') : t('DocumentEdit.Scoring.ProcessScoring')}
                </Button>
              </Grid>
              <Grid item>
                <Button onClick={viewScoringHistory} disabled={scoringInProgress || !folderScoringHistoryRead}>
                  <HistoryIcon />
                  {t('OfferEdit.ScoringHistory.Title')}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

DocumentScoringResultComponentAlpha.propTypes = {
  scoringResult: PropTypes.object,
  t: PropTypes.func.isRequired,
  scoringInProgress: PropTypes.bool.isRequired,
  generateScoring: PropTypes.func.isRequired,
  viewScoringHistory: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired
};
export default withTranslation()(withRights(DocumentScoringResultComponentAlpha));
